import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, Receipt } from "lucide-react";
import { PortalShell } from "@/components/AppHeader";
import { api, errMsg, fmtDate, money } from "@/lib/api";

const card = (c) => (c?.last4 ? <><span className="capitalize">{c.brand}</span> •••• {c.last4}</> : "Card on file");

export function ReceiptsPage() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => { api.get("/me/receipts").then(({ data }) => setRows(data)).catch((e) => setError(errMsg(e))); }, []);
  return (
    <PortalShell>
      <div className="mx-auto max-w-3xl space-y-6" data-testid="receipts-page">
        <div>
          <p className="lux-eyebrow">Account</p>
          <h1 className="mt-2 text-3xl font-semibold text-amber-50">Receipts</h1>
          <p className="mt-2 text-sm text-zinc-400">Every food, beverage and alcohol purchase we made for you.</p>
        </div>
        {error && <p data-testid="receipts-error" className="text-sm text-red-300">{error}</p>}
        {!rows && !error && <Loader2 className="h-5 w-5 animate-spin text-amber-400" />}
        {rows?.length === 0 && <p data-testid="receipts-empty" className="lux-card p-6 text-sm text-zinc-400">No purchase receipts yet.</p>}
        <div className="space-y-3">
          {rows?.map((r) => (
            <Link key={r.id} to={`/receipts/${r.id}`} data-testid={`receipt-row-${r.id}`} className="lux-card flex items-center justify-between gap-4 p-4 transition-colors hover:border-amber-500/40">
              <div className="flex min-w-0 items-center gap-3">
                <Receipt className="h-5 w-5 shrink-0 text-amber-400" />
                <div className="min-w-0">
                  <p className="truncate text-sm text-amber-100">{r.note}</p>
                  <p className="text-xs text-zinc-500">{r.number} · {fmtDate(r.charged_at)} · {r.kind === "rental" ? "Rental" : "Trip"} {r.trip_ref}</p>
                </div>
              </div>
              <span className="shrink-0 font-semibold text-amber-200">{money(r.amount)}</span>
            </Link>
          ))}
        </div>
      </div>
    </PortalShell>
  );
}

export function ReceiptDetailPage() {
  const { id } = useParams();
  const [r, setR] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get(`/receipts/${id}`).then(({ data }) => {
      setR(data);
      if (data.has_photo) api.get(`/receipts/${id}/photo`, { responseType: "blob" }).then((p) => setPhoto(URL.createObjectURL(p.data))).catch(() => {});
    }).catch((e) => setError(errMsg(e)));
  }, [id]);
  const row = (k, v, testId) => <div className="flex justify-between gap-4 py-2 text-sm"><span className="text-zinc-500">{k}</span><span data-testid={testId} className="text-right text-zinc-100">{v}</span></div>;
  return (
    <PortalShell>
      <div className="mx-auto max-w-xl" data-testid="receipt-detail-page">
        <Link to="/receipts" data-testid="receipt-back-link" className="text-sm text-amber-300 hover:text-amber-200">← All receipts</Link>
        {error && <p data-testid="receipt-error" className="mt-6 text-sm text-red-300">{error}</p>}
        {r && (
          <div className="lux-card mt-4 p-6 sm:p-8">
            <img src="/logo-mark.png" alt="Luxury Rideshare" className="h-12 w-12 rounded-lg" />
            <p className="mt-3 text-xs tracking-[0.3em] text-amber-400">LUXURY RIDESHARE</p>
            <h1 className="mt-1 text-2xl font-semibold text-amber-50">Purchase receipt</h1>
            <div className="mt-5 divide-y divide-white/5 border-y border-white/10">
              {row("Receipt", r.number, "receipt-number")}
              {row("Date", fmtDate(r.charged_at), "receipt-date")}
              {row(r.kind === "rental" ? "Rental" : "Trip", r.trip_ref, "receipt-trip")}
              {row("Items", r.note, "receipt-items")}
              {row("Card", card(r.card), "receipt-card")}
              {r.fare_method && row("Fare paid with", r.fare_method, "receipt-fare-method")}
            </div>
            <div className="flex justify-between pt-4"><span className="text-amber-300">Total</span><span data-testid="receipt-amount" className="text-2xl font-semibold text-amber-200">{money(r.amount)}</span></div>
            {photo && <img data-testid="receipt-photo" src={photo} alt="Store receipt" className="mt-6 w-full rounded-lg border border-white/10" />}
            <p className="mt-6 text-xs text-zinc-500">Questions? Call <a href="tel:+12145062525" className="text-amber-300">214-506-2525</a>.</p>
          </div>
        )}
      </div>
    </PortalShell>
  );
}
